/**
 * Block-for-block skeleton for the temperature-log workflow page.
 *
 * Mirrors the live layout: workflow header bar, the desktop location
 * rail (mobile chip strip), and the meal-grouped temperature grid with
 * the footer action row; on a phone, the Orders sheet card and the rows of
 * Kitchen / Drop-off tiles (PROD-1511). Per the CLAUDE.md "Loading states"
 * guidance, never render a plain `Loading...` string here.
 */

import { Skeleton } from '@ordo/ui/skeleton'

export function TemperatureLogSkeleton() {
  return (
    <div className="flex h-screen flex-col bg-background" aria-busy="true" aria-label="Loading temperature logs">
      <header className="flex items-center justify-between border-b bg-background px-4 py-3 md:px-6">
        <div className="space-y-1">
          <Skeleton className="h-5 w-36" />
          {/* The phone header dates the day. */}
          <Skeleton className="h-3 w-20 md:hidden" />
        </div>
        <Skeleton className="h-8 w-8 rounded-lg" />
      </header>

      {/* Mobile: location chip strip. */}
      <div className="flex gap-2 border-b px-4 py-3 md:hidden">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-36 rounded-full" />
        ))}
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Desktop: location rail. */}
        <aside className="hidden w-72 shrink-0 border-r p-3 md:block">
          <Skeleton className="mx-3 mb-3 h-3 w-20" />
          <div className="space-y-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-2.5">
                <Skeleton className="h-6 w-6 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <main className="flex-1 overflow-y-auto px-4 py-6 md:px-8">
            {/* Phone (PROD-1511): the location, the Orders sheet card, the divider, then rows of two tiles. */}
            <div className="space-y-5 md:hidden">
              <div className="space-y-2">
                <Skeleton className="h-8 w-56" />
                <Skeleton className="h-4 w-64" />
              </div>
              <div className="flex flex-col gap-3.5 rounded-[14px] border p-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="size-10 rounded-[10px]" />
                  <Skeleton className="h-5 w-32" />
                </div>
                <div className="space-y-1.5">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
                <Skeleton className="h-[110px] w-full rounded-lg" />
                <div className="space-y-1.5">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Skeleton className="h-[84px] rounded-lg" />
                </div>
              </div>
              <Skeleton className="mx-auto h-3 w-36" />
              <div className="rounded-[10px] border">
                <div className="border-b px-4 py-3">
                  <Skeleton className="h-4 w-32" />
                </div>
                <div className="divide-y">
                  {Array.from({ length: 3 }).map((_, row) => (
                    <div key={row} className="flex items-center gap-2.5 p-3">
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                      <Skeleton className="h-[60px] w-[76px] shrink-0 rounded-[10px]" />
                      <Skeleton className="h-[60px] w-[76px] shrink-0 rounded-[10px]" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mx-auto hidden max-w-3xl md:block">
              <div className="mb-6 space-y-2">
                <Skeleton className="h-8 w-80" />
                <Skeleton className="h-4 w-96" />
              </div>

              <Skeleton className="mb-6 h-20 w-full rounded-xl" />

              {Array.from({ length: 2 }).map((_, group) => (
                <div key={group} className="mb-6">
                  <Skeleton className="mb-3 h-5 w-44" />
                  <div className="space-y-2">
                    {Array.from({ length: 2 }).map((_, row) => (
                      <div key={row} className="flex items-center justify-between gap-4 rounded-lg border p-3">
                        <div className="space-y-1.5">
                          <Skeleton className="h-4 w-36" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                        <div className="flex gap-2">
                          <Skeleton className="h-10 w-24 rounded-md" />
                          <Skeleton className="h-10 w-24 rounded-md" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </main>

          <footer className="border-t bg-background px-4 py-4 md:px-8">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
              <Skeleton className="h-3 w-32" />
              <div className="flex gap-2">
                <Skeleton className="h-11 w-32 rounded-full" />
                <Skeleton className="h-11 w-40 rounded-full" />
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  )
}
